import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { needsReviewCount, RxnormFilter, rxnormStateCount } from 'components/shared/highCostMeds/rxnormStatus';
import { NotificationState } from 'components/shared/Notification';
import useHighCostMedicationListImportChannel, {
  HighCostMedicationListImportChannelMessage,
} from 'hooks/useHighCostMedicationListImportChannel';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import { simpleDateAtTime } from 'lib/date';
import {
  HighCostMedicationListImport,
  isTerminalHighCostMedImportStatus,
} from 'services/api/admin/highCostMedicationListImports';
import { useNotificationActions } from 'stores/notificationStore';

type Props = {
  highCostMedicationListImport: HighCostMedicationListImport;
  onSettled: (importId: string) => void;
};

/**
 * Drives one upload's processing -> success/failure notification by subscribing to the import's
 * status channel. The channel transmits the current state on subscribe, so an import that finished
 * before the socket connected isn't missed. The notification lives in the global store, so it
 * persists after this tracker unmounts on settle — an admin who navigates away still sees the
 * outcome.
 */
export default function HighCostMedListImportTracker({ highCostMedicationListImport, onSettled }: Props) {
  const { addNotification, updateNotification } = useNotificationActions();
  const invalidateQueries = useInvalidateQueries();
  const navigate = useNavigate();

  const settledRef = useRef(false);
  const notificationIdRef = useRef<string | null>(null);
  const filename = highCostMedicationListImport.filename ?? 'high cost medication list';

  // Add the processing notification once on mount. This effect runs before the channel
  // subscription's effect (declared below), so the id is set before any status message arrives. The
  // ref guard keeps React StrictMode's double-invoked mount effect from adding it twice.
  useEffect(() => {
    if (notificationIdRef.current) return;

    notificationIdRef.current = addNotification({
      id: highCostMedicationListImport.id,
      state: NotificationState.PROCESSING,
      title: 'Currently processing',
      timestamp: `${filename} - ${simpleDateAtTime(new Date())}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMessage = useCallback(
    (data: HighCostMedicationListImportChannelMessage) => {
      if (settledRef.current || !notificationIdRef.current || !isTerminalHighCostMedImportStatus(data.status)) return;

      settledRef.current = true;
      const notificationId = notificationIdRef.current!;

      if (data.status === 'completed') {
        // The new list now exists: refresh the tab (Medication Count / Last Updated) and drop this
        // client from the eligible-clients picker. The picker is a disabled infinite query, so
        // useInvalidateQueries resets rather than invalidates it.
        invalidateQueries({ adminHighCostMedicationLists: true, highCostMedListEligibleClients: true });
        updateNotification(notificationId, {
          state: NotificationState.SUCCESS,
          title: `${filename} has successfully been uploaded.`,
          ...rxnormNotice(data, navigate),
        });
      } else {
        updateNotification(notificationId, {
          state: NotificationState.FAILURE,
          title: `Upload failed to process for ${filename}`,
          // Surface the importer's specific reason (row number / header / problem) so the admin can
          // fix the CSV.
          timestamp: data.error ?? undefined,
        });
      }

      onSettled(highCostMedicationListImport.id);
    },
    [filename, invalidateQueries, updateNotification, onSettled, highCostMedicationListImport.id, navigate]
  );

  useHighCostMedicationListImportChannel({
    params: { high_cost_medication_list_import_id: highCostMedicationListImport.id },
    onMessageReceived: handleMessage,
  });

  return null;
}

// The coding summary under a completed import, linking to the flagged rows when there are any. Absent
// counts (an api that does not send them yet) leave the notice as it was.
function rxnormNotice(
  { rxnormStateCounts: counts, highCostMedicationListId: listId }: HighCostMedicationListImportChannelMessage,
  navigate: ReturnType<typeof useNavigate>
) {
  if (!counts) return { timestamp: undefined };

  const flagged = rxnormStateCount(counts, 'not_recognized');
  const needsReview = needsReviewCount(counts);
  const unchecked = rxnormStateCount(counts, 'unchecked');
  const summary = [
    flagged > 0 && `${flagged} flagged`,
    needsReview > 0 && `${needsReview} ${needsReview === 1 ? 'needs' : 'need'} review`,
    unchecked > 0 && `${unchecked} not checked yet`,
  ].filter(Boolean);

  const rxnormFilters: RxnormFilter[] = ['flagged'];
  const canReview = !!listId && flagged > 0;

  return {
    timestamp: summary.length ? `${summary.join(' · ')}.` : undefined,
    ...(canReview && {
      actionSuccessText: 'Review them',
      onActionClick: () =>
        navigate(`/admin/admissions/high-cost-medication-lists/${listId}`, { state: { rxnormFilters } }),
    }),
  };
}
